import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "@/types";
import { apiClient } from "@/lib/api";
import { USER_STORAGE_KEY } from "@/lib/constants";

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  login: (username: string, password: string) => Promise<void>;
  loginWithPin: (username: string, pin: string) => Promise<void>;
  logout: () => Promise<void>;
  fetchCurrentUser: () => Promise<void>;
  setUser: (user: User | null) => void;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (username: string, password: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiClient.login(username, password);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : "Login failed";
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });

          throw err;
        }
      },

      loginWithPin: async (username: string, pin: string) => {
        set({ isLoading: true, error: null });

        try {
          const response = await apiClient.loginWithPin(username, pin);
          set({
            user: response.user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
        } catch (err) {
          const message = err instanceof Error ? err.message : "Login failed";
          set({
            user: null,
            isAuthenticated: false,
            isLoading: false,
            error: message,
          });

          throw err;
        }
      },

      logout: async () => {
        await apiClient.logout();
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      },

      fetchCurrentUser: async () => {
        const { isAuthenticated } = get();
        if (!isAuthenticated) return;

        set({ isLoading: true });

        try {
          const user = await apiClient.get<User>("/auth/me");
          set({ user, isLoading: false });
        } catch {
          get().logout();
        }
      },

      setUser: (user: User | null) => {
        set({ user, isAuthenticated: !!user });
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: USER_STORAGE_KEY,
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    },
  ),
);

export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) =>
  state.isAuthenticated;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectAuthError = (state: AuthState) => state.error;

export const usePermissions = () => {
  const user = useAuthStore(selectUser);
  const permissions = user?.role?.permissions?.map((p) => p.slug) ?? [];

  const hasPermission = (permission: string) =>
    permissions.includes(permission);
  const hasAnyPermission = (requiredPermissions: string[]) =>
    requiredPermissions.some((p) => permissions.includes(p));

  return {
    permissions,
    hasPermission,
    hasAnyPermission,
  };
};
